// Lightweight fuzzy matching for autocompletes and filter inputs. A candidate
// matches when the query's characters appear in order (a subsequence,
// case-insensitive); the score rewards contiguous runs and start-of-string
// hits so the tightest matches sort first. No external dep — the option lists
// are small (metric names, aggregations, group prefixes).

const START_BONUS = 5;
const CONTIGUOUS_BONUS = 2;
// Tiny per-character penalty so shorter, denser candidates edge out longer ones.
const LENGTH_PENALTY = 0.01;

/**
 * Score `candidate` against `query`. Returns `null` when `query` is not a
 * subsequence of `candidate`; higher scores are better. An empty query scores
 * 0 (matches everything, no ranking signal).
 */
export function fuzzyScore(query: string, candidate: string): number | null {
  const q = query.toLowerCase();
  const c = candidate.toLowerCase();
  if (q === "") {
    return 0;
  }

  let qi = 0;
  let score = 0;
  let prevMatchIndex = -2;
  for (let ci = 0; ci < c.length && qi < q.length; ci++) {
    if (c[ci] === q[qi]) {
      score += 1;
      if (ci === prevMatchIndex + 1) {
        score += CONTIGUOUS_BONUS;
      }
      if (ci === 0) {
        score += START_BONUS;
      }
      prevMatchIndex = ci;
      qi++;
    }
  }

  if (qi < q.length) {
    return null;
  }
  return score - c.length * LENGTH_PENALTY;
}

/**
 * Return the indices in `candidate` that match the characters of `query` (same
 * greedy left-to-right pass as `fuzzyScore`). Returns `null` when `query` is
 * not a subsequence; an empty query yields `[]`.
 */
export function fuzzyMatchIndices(
  query: string,
  candidate: string,
): number[] | null {
  const q = query.toLowerCase();
  const c = candidate.toLowerCase();
  if (q === "") {
    return [];
  }

  const indices: number[] = [];
  let qi = 0;
  for (let ci = 0; ci < c.length && qi < q.length; ci++) {
    if (c[ci] === q[qi]) {
      indices.push(ci);
      qi++;
    }
  }

  return qi < q.length ? null : indices;
}

/**
 * Like `fuzzyMatchIndices`, but returns a partial result when the candidate is
 * too short to consume the entire query. The returned array contains indices for
 * however many query characters *could* be matched — useful for highlighting
 * ancestor nodes that only cover a prefix of the full query.
 */
export function fuzzyMatchIndicesPartial(
  query: string,
  candidate: string,
): number[] {
  const q = query.toLowerCase();
  const c = candidate.toLowerCase();
  if (q === "") {
    return [];
  }

  const indices: number[] = [];
  let qi = 0;
  for (let ci = 0; ci < c.length && qi < q.length; ci++) {
    if (c[ci] === q[qi]) {
      indices.push(ci);
      qi++;
    }
  }

  return indices;
}

/**
 * Filter `options` to those fuzzy-matching `query`, ranked best-first. An empty
 * query returns the options unchanged (stable order).
 */
export function fuzzyFilter(query: string, options: string[]): string[] {
  if (query.trim() === "") {
    return [...options];
  }
  const scored: { option: string; score: number }[] = [];
  for (const option of options) {
    const score = fuzzyScore(query, option);
    if (score !== null) {
      scored.push({ option, score });
    }
  }
  scored.sort((a, b) => b.score - a.score);
  return scored.map((entry) => entry.option);
}
